import { useState } from 'react';
import { Box } from '@mui/material';
import { SingleValuePickerV2, SingleValuePickerV2Props } from './SingleValuePicker';
import {
  DATE_RANGE_OPTIONS,
  DATE_RANGE_OPTIONS_HASH,
  DateRangeOptionsValuesType,
  generateDateRange,
} from '@constants';
import { DatePickerV2, DatePickerV2Props, datePickerFormat } from './DatePicker';
import { sub, format } from 'date-fns';

type DateRangeOptionsType = {
  dateRangeOptions: DateRangeOptionsValuesType[];
  dateRangeOptionHash?: typeof DATE_RANGE_OPTIONS_HASH;
  defaultOption: DateRangeOptionsValuesType;
  onDateRangeChange: ({
    newOption,
    newStart,
    newEnd,
  }: {
    newOption: DateRangeOptionsValuesType;
    newStart: string | null | undefined;
    newEnd: string | null | undefined;
  }) => void;
  defaultCustomStart?: string;
  defaultCustomEnd?: string;
  customStartDateParent?: string | null;
  customEndDateParent?: string | null;
};

const DateRangeOptions = ({
  dateRangeOptions,
  dateRangeOptionHash,
  defaultOption,
  defaultCustomStart,
  defaultCustomEnd,
  onDateRangeChange,
  customStartDateParent,
  customEndDateParent,
}: DateRangeOptionsType) => {
  const [{ dateRangeOption, customStartDate, customEndDate }, setOptions] = useState<{
    dateRangeOption: DateRangeOptionsValuesType;
    customStartDate: Parameters<DateRangeOptionsType['onDateRangeChange']>[0]['newStart'];
    customEndDate: Parameters<DateRangeOptionsType['onDateRangeChange']>[0]['newEnd'];
  }>({
    dateRangeOption: defaultOption,
    customStartDate:
      defaultCustomStart ?? format(sub(new Date(), { months: 12 }), datePickerFormat),
    customEndDate: defaultCustomEnd ?? format(new Date(), datePickerFormat),
  });

  const customStartDateToUse = customStartDateParent ?? customStartDate;
  const customEndDateToUse = customEndDateParent ?? customEndDate;

  const onDateRangeOptionChange: SingleValuePickerV2Props['onValueChange'] = (event) => {
    const { value: newDateRange } = event.target;
    const isNewOption = newDateRange !== dateRangeOption;

    if (isNewOption) {
      const isNewDateRangeCustom = newDateRange === DATE_RANGE_OPTIONS.CUSTOM;
      const { start_date, end_date } = generateDateRange({
        option: newDateRange,
        customStartDate,
        customEndDate,
      });

      setOptions((prev) => ({
        ...prev,
        dateRangeOption: newDateRange,
      }));

      onDateRangeChange({
        newOption: newDateRange,
        newStart: isNewDateRangeCustom ? customStartDate : start_date,
        newEnd: isNewDateRangeCustom ? customEndDate : end_date,
      });
    }
  };

  const onCustomStartDateChange: DatePickerV2Props['onChange'] = (newDate) => {
    setOptions((prev) => ({ ...prev, customStartDate: newDate }));

    onDateRangeChange({
      newOption: dateRangeOption,
      newStart: newDate,
      newEnd: customEndDateToUse,
    });
  };

  const onCustomEndDateChange: DatePickerV2Props['onChange'] = (newDate) => {
    setOptions((prev) => ({ ...prev, customEndDate: newDate }));

    onDateRangeChange({
      newOption: dateRangeOption,
      newStart: customStartDateToUse,
      newEnd: newDate,
    });
  };

  return (
    <>
      <Box>
        {dateRangeOptionHash ? (
          <SingleValuePickerV2
            items={dateRangeOptions}
            itemsHash={dateRangeOptionHash}
            item={dateRangeOption}
            onValueChange={onDateRangeOptionChange}
            inputLabel="Date Range"
          />
        ) : (
          <></>
        )}
      </Box>
      {dateRangeOption === DATE_RANGE_OPTIONS.CUSTOM ? (
        <>
          {' '}
          <DatePickerV2
            label={'Start Date'}
            date={customStartDateToUse}
            onChange={onCustomStartDateChange}
          />
          <DatePickerV2
            label={'End Date'}
            date={customEndDateToUse}
            onChange={onCustomEndDateChange}
          />
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default DateRangeOptions;
